import * as a from '@mitranim/js/all.mjs'
import * as c from './conf.mjs'
import * as um from './util-misc.mjs'
import * as uma from './util-math.mjs'
import * as ut from './util-text.mjs'
import * as uti from './util-time.mjs'
import * as uc from './util-coll.mjs'
import * as ue from './util-err.mjs'

export async function requireUser(ctx) {
  return (
    (await ctx.dat.user.valueTask()) ??
    a.panic(errUnauth())
  )
}

export function errUnauth() {
  return new ue.ErrHttp(`authentication required`, c.HTTP_UNAUTHORIZED)
}

export async function requireUserFresh(ctx) {
  await ctx.dat.user.valueTaskFresh()
  return requireUser(ctx)
}

export async function requirePaidListing(ctx) {
  const user = await requireUser(ctx)
  if (user.hasUnpaidListing()) {
    throw ue.errRedirTemp(ut.url(c.LINK_PLANS).setHash(c.ELEM_ID_PLANS))
  }
}

export async function requireInstallMobileApp(ctx) {
  const user = await requireUser(ctx)
  if (!c.REQUIRE_MOBILE_APP) return
  if (user.canReceiveMobileNotifications()) return
  throw ue.errRedirTemp(ut.url(c.LINK_MY_INSTALL_MOBILE_APP).queryPatch({redir: ctx.url}))
}

export async function requirePract(ctx) {
  const user = await requireUser(ctx)
  if (!isPract(user)) {
    throw new ue.Err({
      message: `must be practitioner`,
      status: c.HTTP_FORBIDDEN,
      code: c.ERR_CODE_REQUIRES_PRACT,
    })
  }
  return user
}

export function practIntLink(id) {
  return ut.maybeUrl(`/practitioners`, id)
}

export function practLink(person) {
  return (
    ut.toUrlOpt(personToSlugPath(person)) ||
    // TODO rip out, always use `.slug` or `.id`.
    practIntLink(a.laxStruct(person).practitionerIntId)
  )
}

export function fakeOrgLink(org) {
  return (
    ut.toUrlOpt(personToSlugPath(org)) ||
    // TODO rip out, always use `.slug` or `.id`.
    ut.maybeUrl(c.LINK_ORGANIZATIONS, a.laxStruct(org).practitionerIntId)
  )
}

export function guessPersonLink(person) {
  if (!isPractModerationStatusApproved(person)) return undefined
  return isFakeOrg(person) ? fakeOrgLink(person) : practLink(person)
}

// At the time of writing, `/@slug` routes are supported only for practitioners.
export function personToSlugPath(val) {
  a.optStruct(val)
  if (val?.isPractitioner) return slugPersonPath(val?.slug)
  return undefined
}

/*
If the person has a slug, returns a path like `/@slug`. Otherwise returns
``. Note: works for ANY person, including organizations.
*/
export function slugPersonPath(val) {
  val = ut.slugMention(val)
  return val && (`/` + val)
}

export function guessPersonNameOpt(src) {
  if (!a.optStruct(src)) return undefined

  const name = src.name
  if (name) return name

  const slug = src.slug
  if (slug) return ut.slugMention(slug)

  const email = src.email
  if (email) return email

  return undefined
}

/*
Note: a person may have neither a name nor an email. We should always be
prepared to use a fallback.
*/
export function guessPersonName(src) {
  if (a.optStruct(src)) {
    const out = guessPersonNameOpt(src)
    if (out) return out

    // TODO: this should be rendered with special formatting, like gray color.
    if (src.deletedAt) return `(deleted)`

    const id = src.id
    if (id) return id
  }

  // TODO: this should be rendered with special formatting, like gray color.
  return `(anonymous)`
}

// This is wrong. We should allow orgs to set avatars, and always use avatar
// images in appropriate contexts.
//
// Possible to upgrade to organization, keep avatar and bypass moderation. Replaced from:
// `return isFakeOrg(person) ? person.coverImage : person.avatarImage`
export function guessPersonImage(person) {
  person = a.laxStruct(person)
  return isFakeOrg(person) && person.coverImage || person.avatarImage || person.coverImage
}

export function isPract(person) {
  return a.truthy(a.laxStruct(person).isPractitioner)
}

export function isFakeOrg(person) {
  return a.truthy(a.laxStruct(person).isOrganization) || a.truthy(a.laxStruct(person).organizationId)
}

export function isPractOrOrg(person) {
  return isPract(person) || isFakeOrg(person)
}

export function isPractModerationStatusApproved(person) {
  return isPractOrOrg(person) && person?.moderationStatus === c.MODERATION_STATUS_APPROVED
}

export function hasBigPin(person) {
  return person.isPaidPromoActive(c.SLUG_PROMOTION_STEAL_THE_MAP)
}

export function isFavoritePractitioner(person) {
  return person.isPaidPromoActive(c.SLUG_PROMOTION_FAVORITE_PRACTITIONERS)
}

export function isPreferredPractitioner(person) {
  return person.isPaidPromoActive(c.SLUG_PROMOTION_PREFERRED_PRACTITIONERS)
}

export function isPromotionOrderItemActive(item) {
  return (
    item?.promotionOrder?.status === c.PURCHASE_STATUS_PAID &&
    uti.isDateBetween(Date.now(), item.startAt, item.endAt)
  )
}

export function homeLink() {return ut.maybeUrl(c.LINK_HOME)}

export function isPromotionPurchaseActive(item) {
  return (
    item?.status === c.PURCHASE_STATUS_PAID &&
    uti.isDateBetween(Date.now(), item.activeFrom, item.activeTill)
  )
}

export function articleLink(slug) {return ut.maybeUrl(c.LINK_ARTICLES, slug)}
export function articleEntLink(ent) {return articleLink(um.getSlug(ent))}

export function articleEntEditLink(article) {
  return ut.maybeUrl(c.LINK_MY_ARTICLES, um.getSlug(article), `edit`)
}

export function articleEntModMsgsLink(article) {
  return ut.maybeUrl(c.LINK_MY_ARTICLES, um.getSlug(article), `moderation`)
}

export function servicesLink() {return ut.maybeUrl(c.LINK_SERVICES)}
export function serviceLink(key) {return ut.maybeUrl(c.LINK_SERVICES, key)}
export function serviceEntLink(ent) {return serviceLink(um.getSlug(ent))}

export function serviceVidId(val) {return a.head(a.laxStruct(val).vidIds)}
export function serviceBookFileId(val) {return a.head(a.laxStruct(val).bookFileIds)}

// WTB shorter name.
function serviceNatureManagementLinkBase(val) {
  val = a.optStruct(val)?.nature
  if (val === c.SERVICE_NATURE_EVENT)  return c.LINK_MY_EVENTS
  if (val === c.SERVICE_NATURE_VIDEO)  return c.LINK_MY_VIDEOS
  if (val === c.SERVICE_NATURE_BOOK)   return c.LINK_MY_BOOKS
  if (val === c.SERVICE_NATURE_COURSE) return c.LINK_MY_COURSES
  if (val === c.SERVICE_NATURE_SUB)    return c.LINK_MY_COURSE_SECTIONS
  if (val) return c.LINK_MY_SERVICES
  return undefined
}

export function serviceEditLink(val) {
  return ut.url(serviceNatureManagementLinkBase(val), um.getId(val), `edit`)
}

export function serviceModerationLink(val) {
  return ut.url(serviceNatureManagementLinkBase(val), um.getSlug(val), `moderation`)
}

export function servicePublishLink(val) {
  const slug = um.getSlug(val)
  a.reqValidStr(slug)
  return ut.maybeUrl(c.LINK_MY_SERVICES, slug, `publish`)
}

export function eventSchedLink(id) {
  return ut.maybeUrl(c.LINK_MY_EVENTS, id, `sched`)
}

export function serviceVideoEditLink(val) {
  return ut.maybeUrl(c.LINK_MY_VIDEOS, um.getId(val), `edit`)
}

export function serviceVideoUploadLink(id) {
  return ut.maybeUrl(c.LINK_MY_VIDEOS, id, `upload`)
}

export function serviceBookEditLink(id) {
  return ut.maybeUrl(c.LINK_MY_BOOKS, id, `edit`)
}

export function serviceCourseEditLink(id) {
  return ut.maybeUrl(c.LINK_MY_COURSES, id, `edit`)?.href
}

export function serviceCourseOverviewLink(id) {
  return ut.maybeUrl(c.LINK_MY_COURSES, id)?.href
}

export function serviceCourseSectionCreateLink(originId) {
  return ut.maybeUrl(c.LINK_MY_COURSES, originId, `sections/create`)?.href
}

export function serviceCourseSectionEditLink(originId) {
  return ut.maybeUrl(c.LINK_MY_COURSE_SECTIONS, originId, `edit`)?.href
}

export function videoAndProductsLink(val) {return serviceEntLink(val)}

export function serviceReviewLink(service) {
  const {originId} = a.laxStruct(service)
  return ut.maybeUrl(c.LINK_MY_PURCHASES_SERVICES, originId, `review`)
}

export function bookingBuyerReviewLink(booking) {
  return calAbsTimespanBuyerReviewLink(a.laxStruct(booking).calAbsTimespanId)
}

export function bookingSellerReviewLink(booking) {
  return calAbsTimespanSellerReviewLink(a.laxStruct(booking).calAbsTimespanId)
}

export function calAbsTimespanBuyerReviewLink(id) {
  return ut.maybeUrl(c.LINK_MY_PURCHASES_SERVICES_REVIEWS, id, `buyer`)
}

export function calAbsTimespanSellerReviewLink(id) {
  return ut.maybeUrl(c.LINK_MY_SERVICES_BOOKINGS_REVIEWS, id, `seller`)
}

export function isServiceYoutube() {return false}

export function isServiceConference(val) {
  return a.laxStruct(val).nature === c.SERVICE_NATURE_SESSION && !!val.isOnline
}

export function isServiceVideo(val) {
  return a.laxStruct(val).nature === c.SERVICE_NATURE_VIDEO
}

export function isServiceEvent(val) {
  return a.laxStruct(val).nature === c.SERVICE_NATURE_EVENT
}

export function isServiceBook(val) {
  return a.laxStruct(val).nature === c.SERVICE_NATURE_BOOK
}

export function isServiceSession(val) {
  return a.laxStruct(val).nature === c.SERVICE_NATURE_SESSION
}

export function isServiceCourse(val) {
  return a.laxStruct(val).nature === c.SERVICE_NATURE_COURSE
}

export function isServiceCourseSection(val) {
  return a.laxStruct(val).nature === c.SERVICE_NATURE_SUB
}

export function isServiceGroup(val) {
  return a.laxStruct(val).relationType !== c.SERVICE_RELATION_TYPE_ONE_TO_ONE
}

// Redundant with `Service..isOwnedBy`, TODO dedup.
export function isServiceOwnedByPerson(service, person) {
  return um.teq(um.getId(person), a.laxStruct(service).personId)
}

export function serviceGeo(val) {
  return validOrMissingGeo(val) || validOrMissingGeo(a.laxStruct(val).person)
}

// These fields are standardized on the server. The input may be a person, a
// service, etc.
export function validOrMissingGeo(val) {
  const {geoName, geoPoint} = a.laxStruct(val)

  return geoName && um.isGeoPoint(geoPoint)
    ? {geoName, geoPoint}
    : undefined
}

export function reviewSubjectService       (val) {return {subjectServiceOriginId: val.originId}}
export function reviewSubjectArticle       (val) {return {subjectArticleOriginId: val.originId}}
export function reviewSubjectPerson        (val) {return {subjectPersonId:        val.id}}
export function reviewSubjectRelatedPerson (val) {return {subjectPersonId:        val.personId}}

export function isMsgAuthor(msg, user) {
  const author = a.laxStruct(msg).person
  return um.teq(um.getId(author), um.getId(user))
}

// TODO move to `dat-cat.mjs`.
export function entityCatFilters(val) {
  return entityCatSlugs(val) || entityCatIds(val)
}

function entityCatSlugs(val) {
  val = a.laxStruct(val)
  return uc.vacDict({
    mainCategorySlugs: um.getSlugs(val.mainShortCategories),
    concernCategorySlugs: um.getSlugs(val.concernShortCategories),
  })
}

// Warning: avoid this for URLs. URL category filters must always use slugs.
function entityCatIds(val) {
  val = a.laxStruct(val)
  return uc.vacDict({
    mainCategoryIds: val.mainCategoryIds,
    concernCategoryIds: val.concernCategoryIds,
  })
}

export function isSubscriptionPurchaseActive(val) {
  return (
    a.laxStruct(val).status === c.PURCHASE_STATUS_PAID &&
    uti.isDateBetween(Date.now(), val.activeFrom, val.activeTill)
  )
}

export function isSubscriptionActiveMembership(val) {
  return isSubscriptionPurchaseActive(val) && val.discount?.type === `prime`
}

// Very brittle, TODO revise.
export function selectedLink(pathname, item) {
  if (!pathname && !item) return null

  return (
    pathname === c.LINK_SERVICES  ? serviceEntLink(item) :
    pathname === c.LINK_EVENTS    ? serviceEntLink(item) :
    pathname === c.LINK_ARTICLES  ? articleEntLink(item) :
    pathname === c.LINK_VIDEOS    ? videoAndProductsLink(item) :
    pathname === c.LINK_PRODUCTS  ? videoAndProductsLink(item) :
    null
  )
}

/*
One fake like per N hours.

We don't want to be a ghost site with zero likes everywhere; at the same time we
don't want to fake recently-published entities.
*/
export function fakeLikesSince(date) {
  if (a.isNil(date)) return 0
  const hours = uti.hoursAgo(date)
  return uma.round(Math.sqrt(hours)) || 0
}

export async function serviceLangs(ctx, service, seller) {
  const languageCode = service?.languageCode
  const languageCodes = seller?.languageCodes
  if (!a.vac(languageCode) && !a.vac(languageCodes)) return undefined

  const langs = await ctx.dat.languages.valueTask()

  if (isServiceSession(service)) {
    return ut.joinComma(a.map(languageCodes, val => langs.getReq(val).title))
  }

  return langs.getReq(languageCode).title
}

export function ageRestrictionTitle(val) {
  const sel = findByValue(c.SERVICE_PARTICIPANT_AGE_SELECTABLES, val)
  return a.laxStruct(sel).title || val
}

function findByValue(src, val) {
  return a.find(src, src => src.value === val)
}

export function isAccessOrderPaid(val) {
  return a.laxStruct(val).status === c.PURCHASE_STATUS_PAID
}

export function luminaryLink(val) {
  return ut.maybeUrl(`/luminaries`, um.getSlug(val))
}

export function personRefferPathname({refCode}) {
  return ut.url(c.LINK_SERVICES).setQuery({ref_code: refCode})
}

export async function maybeRedirLinkToBuyMembership(ctx) {
  const user = await requireUser(ctx)
  const unpaid = user.hasUnpaidListing()

  if (
    (a.isNil(unpaid) || unpaid)
    // && ctx.env.disk.bank().wroteMissingBits(c.DISK_BITS_CLOSED_BUY_MEMBERSHIP)
  ) {
    return ctx.url.withPathname(c.LINK_EMBED_BUY_MEMBERSHIP)
  }
  return undefined
}

export async function userEnablePromoNag(ctx) {
  return (await ctx.dat.user.valueTask())?.privateMeta?.enablePromoNag
}
